import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { modalUpdate } from '../../../store/actions/settingsActions';
import { updateActiveManagementTicketId } from '../../../store/actions/ticketsActions';
import { isSupervisorUser } from '../../../store/helpers';
import {
    SERVICE_BREACH_TYPE,
    buildServiceWaitingRows,
    formatElapsedSeconds,
    getActiveServiceSeconds,
    secondsToMinutes,
} from './ServiceWaitingTimes';

const formatSeconds = (seconds) => `${secondsToMinutes(seconds)} min`;
const getTicketLabel = (ticket) => `${ticket.ticketLetter || ''}${ticket.ticketNumber || ''}`;

export const buildServicesNeedingAttentionRows = (serviceRows = [], now = Math.floor(Date.now() / 1000)) => serviceRows
    .flatMap(row => row.breachedTickets.map((ticket) => {
        const breach = ticket.slaBreach && ticket.slaBreach.breachType === SERVICE_BREACH_TYPE ? ticket.slaBreach : {};
        const activeServiceSeconds = getActiveServiceSeconds(ticket, now);
        const thresholdSeconds = Number.isFinite(Number(breach.thresholdSeconds))
            ? Number(breach.thresholdSeconds)
            : row.serviceThresholdSeconds;
        const overSeconds = Math.max(0, activeServiceSeconds - (thresholdSeconds || 0));
        const breachedAt = Number.isFinite(Number(breach.breachedAt))
            ? Number(breach.breachedAt)
            : null;

        return {
            ticket,
            ticketId: ticket.id,
            ticketLabel: getTicketLabel(ticket),
            serviceId: row.serviceId,
            serviceName: row.serviceName,
            avgServiceSeconds: Number.isFinite(Number(breach.avgServiceSeconds))
                ? Number(breach.avgServiceSeconds)
                : row.serviceDurationSeconds,
            activeServiceSeconds,
            thresholdSeconds,
            overSeconds,
            breachedAt,
        };
    }))
    .sort((a, b) => {
        if (b.overSeconds !== a.overSeconds) {
            return b.overSeconds - a.overSeconds;
        }
        return b.activeServiceSeconds - a.activeServiceSeconds;
    });

function SlaBreachedTickets() {
    const dispatch = useDispatch();
    const [now, setNow] = useState(Math.floor(Date.now() / 1000));
    const { authenticatedUser } = useSelector(state => state.auth);
    const { serviceWaitingTimes, services } = useSelector(state => state.services);
    const { tickets } = useSelector(state => state.tickets);

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Math.floor(Date.now() / 1000));
        }, 15000);
        return () => clearInterval(interval);
    }, []);

    if (!isSupervisorUser(authenticatedUser)) {
        return null;
    }

    const serviceRows = buildServiceWaitingRows({
        authenticatedUser,
        now,
        serviceWaitingTimes,
        services,
        tickets,
    });
    const attentionRows = buildServicesNeedingAttentionRows(serviceRows, now);

    const openTicket = (ticketId) => {
        if (!ticketId) {
            return;
        }
        dispatch(updateActiveManagementTicketId(ticketId));
        dispatch(modalUpdate({ visible: 1, content: "TicketManagement", modalType: 1 }));
    };

    return (
        <div className="supervisorPanel supervisorPanelCompact">
            <div className="supervisorPanelHeader">
                <div className="titleBlock">
                    <h4>Service SLA breach</h4>
                </div>
            </div>
            <div className="supervisorAttentionList">
                {attentionRows.map(row => (
                    <div className="supervisorAttentionItem" key={row.ticketId}>
                        <div className="supervisorAttentionMain">
                            <div className="supervisorAttentionTicket">{row.ticketLabel}</div>
                            <div className="supervisorAttentionService">{row.serviceName}</div>
                        </div>
                        <div className="supervisorAttentionSummaryMetrics">
                            <div>
                                <span>Active</span>
                                <strong>{formatElapsedSeconds(row.activeServiceSeconds)}</strong>
                            </div>
                            <div className="supervisorAttentionSummaryOver">
                                <span>Over SLA</span>
                                <strong>+{formatElapsedSeconds(row.overSeconds)}</strong>
                            </div>
                        </div>
                        <div className="supervisorAttentionMetrics">
                            <div>
                                <span>Avg Service</span>
                                <strong>{formatSeconds(row.avgServiceSeconds)}</strong>
                            </div>
                            <div>
                                <span>SLA Limit</span>
                                <strong>{formatSeconds(row.thresholdSeconds)}</strong>
                            </div>
                            <div className="supervisorAttentionOver">
                                <span>Breached For</span>
                                <strong>{row.breachedAt ? formatElapsedSeconds(now - row.breachedAt) : formatElapsedSeconds(row.overSeconds)}</strong>
                            </div>
                        </div>
                        <div className="supervisorAttentionActions">
                            <button
                                type="button"
                                className="supervisorAttentionButton"
                                onClick={() => openTicket(row.ticketId)}
                            >
                                Open ticket
                            </button>
                        </div>
                    </div>
                ))}
                {!attentionRows.length ? (
                    <div className="supervisorAttentionEmpty">
                        No tickets are breaching their service SLA
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default SlaBreachedTickets;
